import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('uncut_token'));
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('uncut_token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = useCallback(async (newToken) => {
    localStorage.setItem('uncut_token', newToken);
    setToken(newToken);
    
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${newToken}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user after login:', error);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('uncut_token');
    setToken(null);
    setUser(null);
  }, []);

  const sendMagicLink = useCallback(async (email) => {
    const response = await axios.post(`${API}/auth/magic-link`, { email });
    return response.data;
  }, []);

  const verifyMagicLink = useCallback(async (linkToken) => {
    const response = await axios.post(`${API}/auth/verify`, { token: linkToken });
    await login(response.data.token);
    // Return the full response including user data for redirect logic
    return response.data;
  }, [login]);

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    sendMagicLink,
    verifyMagicLink,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin || false
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
